import { PrintInk, PrintMethod, PrintProfile, PrintProfileApi } from '@/types/PrintProfile';

const VALID_METHODS: PrintMethod[] = ['digital', 'screen', 'laser', 'embroidery'];

/**
 * Mapper des profils d'impression (`product.printProfiles[]`).
 *
 * Mirroir de MaterialDetailsMapper : normalise la forme brute backend en
 * `PrintProfile` sur (defauts surs, methode validee, palette/encres nettoyees).
 * Un profil `screen` sans palette exploitable ou `colorCount` valide est
 * ignore (retourne null) car il rendrait le moteur de quantification incoherent.
 */
export class PrintProfileMapper {
    /** Profil "digital implicite", toujours disponible (id reserve). */
    static readonly IMPLICIT_DIGITAL_ID = '__digital_implicit__';

    /**
     * Construit le profil digital synthetique. C'est la selection par defaut :
     * tant qu'il est actif, aucune contrainte n'est appliquee (comportement
     * historique 100% preserve).
     */
    static createImplicitDigitalProfile(name = 'Digital'): PrintProfile {
        return {
            id: PrintProfileMapper.IMPLICIT_DIGITAL_ID,
            name,
            method: 'digital',
            colorCount: null,
            palette: null,
            monochrome: false,
            format: null,
            dpi: null,
            colorSpace: null,
            vector: false,
            bleedMm: null,
            minStrokeMm: null,
            minFontPt: null,
            isImplicitDigital: true
        };
    }

    static getPrintProfilesFromData(
        profiles: Array<PrintProfileApi | null | undefined> | null | undefined
    ): PrintProfile[] {
        const safe = (profiles || []).filter((p): p is PrintProfileApi => !!p);
        const mapped: PrintProfile[] = [];
        for (const raw of safe) {
            const profile = PrintProfileMapper.mapProfile(raw);
            if (profile) {
                mapped.push(profile);
            }
        }
        return mapped;
    }

    private static mapProfile(raw: PrintProfileApi): PrintProfile | null {
        const method = PrintProfileMapper.normalizeMethod(raw.method);
        if (!method) {
            console.warn(`[PrintProfileMapper] methode inconnue ignoree: ${raw.method}`);
            return null;
        }

        const palette = PrintProfileMapper.mapPalette(raw.palette);
        const colorCount =
            typeof raw.colorCount === 'number' && Number.isFinite(raw.colorCount) && raw.colorCount > 0
                ? Math.floor(raw.colorCount)
                : null;

        // Un profil serigraphie/broderie sans palette utilisable OU sans nombre
        // d'encres/fils valide est inexploitable par le moteur de quantification.
        if (
            (method === 'screen' || method === 'embroidery') &&
            (!palette || palette.inks.length === 0 || !colorCount)
        ) {
            console.warn(`[PrintProfileMapper] profil ${method} ignore (palette/colorCount invalide): ${raw.id}`);
            return null;
        }

        return {
            id: raw.id || `${method}_${Math.random().toString(36).slice(2, 9)}`,
            name: raw.name || PrintProfileMapper.defaultNameForMethod(method),
            method,
            colorCount,
            palette,
            monochrome: Boolean(raw.monochrome) || method === 'laser',
            // Numérique sans blanc (BO). Accepte plusieurs clés backend possibles.
            whiteDisabled: Boolean(
                (raw as { whiteDisabled?: boolean; white_disabled?: boolean; noWhite?: boolean }).whiteDisabled ??
                (raw as { white_disabled?: boolean }).white_disabled ??
                (raw as { noWhite?: boolean }).noWhite
            ),
            // Effet de rendu broderie (tatami). ABSENT => activé (forward-compat : les
            // profils existants gardent l'effet). Seul un `false` explicite le désactive.
            embroideryRenderEnabled:
                ((raw as { embroideryRenderEnabled?: boolean }).embroideryRenderEnabled ??
                    (raw as { embroidery_render_enabled?: boolean }).embroidery_render_enabled) !== false,
            // Texte variable (VDP) autorisé pour cette méthode. ABSENT => autorisé
            // (forward-compat) ; seul un `false` explicite (BO) le bloque.
            vdpEnabled:
                ((raw as { vdpEnabled?: boolean }).vdpEnabled ?? (raw as { vdp_enabled?: boolean }).vdp_enabled) !==
                false,
            // Broderie : mode de comptage des fils ('max' => colorCount = plafond).
            threadCountMode:
                ((raw as { threadCountMode?: string }).threadCountMode ??
                    (raw as { thread_count_mode?: string }).thread_count_mode) === 'max'
                    ? 'max'
                    : 'fixed',
            format: raw.format ?? null,
            dpi: typeof raw.dpi === 'number' ? raw.dpi : raw.dpi != null ? Number(raw.dpi) || null : null,
            colorSpace: raw.colorSpace ?? null,
            vector: Boolean(raw.vector),
            bleedMm: PrintProfileMapper.toNumberOrNull(raw.bleedMm),
            minStrokeMm: PrintProfileMapper.toNumberOrNull(raw.minStrokeMm),
            minFontPt: PrintProfileMapper.toNumberOrNull(raw.minFontPt),
            // Contenu vitrine (étape choix de méthode) — best-effort, optionnel.
            imageUrl: raw.imageUrl ?? null,
            title: Array.isArray(raw.title) ? raw.title : null,
            description: Array.isArray(raw.description) ? raw.description : null
        };
    }

    private static mapPalette(palette: PrintProfileApi['palette']): PrintProfile['palette'] {
        if (!palette) {
            return null;
        }
        const inks: PrintInk[] = (palette.inks || [])
            .filter((ink): ink is NonNullable<typeof ink> => !!ink && typeof ink.hex === 'string')
            .map((ink, index) => ({
                id: ink.id || `ink_${index}`,
                name: ink.name || `Encre ${index + 1}`,
                hex: PrintProfileMapper.normalizeHex(ink.hex as string),
                pantoneRef: ink.pantoneRef ?? null,
                position: typeof ink.position === 'number' ? ink.position : index
            }))
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

        if (inks.length === 0) {
            return null;
        }
        return {
            id: palette.id || 'palette',
            name: palette.name || 'Palette',
            inks
        };
    }

    private static normalizeMethod(method: PrintMethod | string | undefined): PrintMethod | null {
        if (!method) {
            return null;
        }
        const lower = String(method).toLowerCase().trim();
        return (VALID_METHODS as string[]).includes(lower) ? (lower as PrintMethod) : null;
    }

    private static defaultNameForMethod(method: PrintMethod): string {
        switch (method) {
            case 'screen':
                return 'Serigraphie';
            case 'laser':
                return 'Gravure laser';
            case 'embroidery':
                return 'Broderie';
            default:
                return 'Digital';
        }
    }

    /** Normalise une couleur hex en `#rrggbb` minuscule (best-effort). */
    static normalizeHex(hex: string): string {
        let value = String(hex || '').trim();
        if (!value.startsWith('#')) {
            value = `#${value}`;
        }
        // Expansion des formes courtes #rgb -> #rrggbb.
        if (/^#[0-9a-fA-F]{3}$/.test(value)) {
            value = `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
        }
        return /^#[0-9a-fA-F]{6}$/.test(value) ? value.toLowerCase() : '#000000';
    }

    private static toNumberOrNull(value: unknown): number | null {
        if (value == null || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
}
